/**
 * Dietician Profile Hook
 *
 * Custom React hook for managing dietician profile state and operations.
 * Uses Zustand store for centralized state management and respects
 * MFA requirements to prevent infinite API call loops.
 *
 * @module hooks/useDieticianProfile
 */

import { useEffect, useCallback, useRef } from 'react';
import { useDieticianProfileStore } from '@/stores/dieticianProfileStore';
import { useMfaStore } from '@/stores/mfaStore';
import { DieticianApiError } from '@/services/domains/DieticianDomain';
import type { DieticianProfileApiResponse } from '@/types/dieticianRegistration';

/**
 * Dietician profile hook state interface
 */
export interface UseDieticianProfileState {
  /** Current dietician profile data */
  profile: DieticianProfileApiResponse | null;
  /** Loading state for profile operations */
  isLoading: boolean;
  /** Error state for profile operations */
  error: DieticianApiError | null;
  /** Whether profile data has been loaded at least once */
  isInitialized: boolean;
  /** Whether a refresh operation is in progress */
  isRefreshing: boolean;
}

/**
 * Dietician profile hook actions interface
 */
export interface UseDieticianProfileActions {
  /** Fetch current dietician profile */
  fetchProfile: () => Promise<void>;
  /** Refresh profile data with retry mechanism */
  refreshProfile: (maxRetries?: number) => Promise<void>;
  /** Clear error state */
  clearError: () => void;
  /** Reset all state to initial values */
  reset: () => void;
}

/**
 * Dietician profile hook return type
 */
export interface UseDieticianProfileReturn extends UseDieticianProfileState, UseDieticianProfileActions {}

/**
 * Hook options interface
 */
export interface UseDieticianProfileOptions {
  /** Whether to automatically fetch profile on mount */
  autoFetch?: boolean;
  /** Callback when profile is successfully loaded */
  onProfileLoaded?: (profile: DieticianProfileApiResponse) => void;
  /** Callback when an error occurs */
  onError?: (error: DieticianApiError) => void;
}

/**
 * Custom hook for dietician profile management
 *
 * Uses Zustand store for centralized state management.
 * Respects MFA requirements - won't fetch when MFA is required.
 *
 * @param options Configuration options for the hook
 * @returns Dietician profile state and actions
 */
export function useDieticianProfile(options: UseDieticianProfileOptions = {}): UseDieticianProfileReturn {
  const {
    autoFetch = true,
    onProfileLoaded,
    onError,
  } = options;

  // Get state from Zustand store
  const profile = useDieticianProfileStore((state) => state.profile);
  const status = useDieticianProfileStore((state) => state.status);
  const error = useDieticianProfileStore((state) => state.error);
  const isInitialized = useDieticianProfileStore((state) => state.isInitialized);

  // Get MFA state to check if we should skip fetching
  const mfaRequired = useMfaStore((state) => state.mfaRequired);

  // Store actions
  const storeFetchProfile = useDieticianProfileStore((state) => state.fetchProfile);
  const storeClearError = useDieticianProfileStore((state) => state.clearError);
  const storeReset = useDieticianProfileStore((state) => state.reset);

  // Refs for callbacks and preventing stale closures
  const onProfileLoadedRef = useRef(onProfileLoaded);
  const onErrorRef = useRef(onError);
  const previousProfileRef = useRef(profile);

  // Keep refs updated
  useEffect(() => {
    onProfileLoadedRef.current = onProfileLoaded;
    onErrorRef.current = onError;
  }, [onProfileLoaded, onError]);

  // Call onProfileLoaded when profile changes from null to a value
  useEffect(() => {
    if (profile && !previousProfileRef.current && onProfileLoadedRef.current) {
      onProfileLoadedRef.current(profile);
    }
    previousProfileRef.current = profile;
  }, [profile]);

  // Call onError when error changes
  useEffect(() => {
    if (error && onErrorRef.current) {
      onErrorRef.current(error);
    }
  }, [error]);

  /**
   * Fetch current dietician profile
   */
  const fetchProfile = useCallback(async (): Promise<void> => {
    // Don't fetch if MFA is required - redirect will handle it
    if (mfaRequired) {
      return;
    }
    await storeFetchProfile();
  }, [mfaRequired, storeFetchProfile]);

  /**
   * Refresh profile data (same as fetch for Zustand implementation)
   */
  const refreshProfile = useCallback(async (): Promise<void> => {
    // Don't refresh if MFA is required
    if (mfaRequired) {
      return;
    }
    await storeFetchProfile();
  }, [mfaRequired, storeFetchProfile]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    storeClearError();
  }, [storeClearError]);

  /**
   * Reset all state to initial values
   */
  const reset = useCallback(() => {
    storeReset();
  }, [storeReset]);

  // Auto-fetch profile on mount (only if not already initialized and MFA not required)
  useEffect(() => {
    if (autoFetch && !isInitialized && status !== 'loading' && !mfaRequired) {
      storeFetchProfile();
    }
  }, [autoFetch, isInitialized, status, mfaRequired, storeFetchProfile]);

  return {
    // State - derived from Zustand store
    profile,
    isLoading: status === 'loading',
    error,
    isInitialized,
    isRefreshing: status === 'loading', // Same as loading in Zustand impl

    // Actions
    fetchProfile,
    refreshProfile,
    clearError,
    reset,
  };
}